import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, inject, Input, NgZone, OnInit, output, Output, ViewChild} from '@angular/core';
import {CodaltComponent} from '../codalt/codalt.component';
import {Ttm} from '../../classes/ttm';
import {TtmStatus} from '../../classes/ttmstatus';
import {TtmType} from '../../classes/ttmtype';
import {TtmImageService} from '../../services/ttm-image.service';
import {TtmImage} from '../../classes/ttmimage';
import {ImageViewerComponent} from '../image-viewer/image-viewer.component';
import {MatDialog} from '@angular/material/dialog';
import {TtmSvgViewerComponent} from '../ttm-svg-viewer/ttm-svg-viewer.component';
import {TtmService} from '../../services/ttm.service';
import {FileUploadFailDialogComponent} from '../file-upload-fail-dialog/file-upload-fail-dialog.component';
import {ApiErrorResponse} from '../../services/api/api.service';
import {Workorder} from '../../classes/workorder';
import {Utils} from '../../utils.class';
import {TtmTypes} from '../../classes/ttm-types.enum';
import {FormControl, FormGroup} from '@angular/forms';
import {ChangeTtmAmountDialogComponent} from '../change-ttm-amount-dialog/change-ttm-amount-dialog.component';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {debounceTime} from 'rxjs/operators';
import {TtmStatusDialogComponent} from '../ttm-status-dialog/ttm-status-dialog.component';
import {Routenames} from '../../routenames';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Features} from '../../services/auth/features';
import {CameraPermissionService} from '../../services/camera-permission.service';

@Component({
    selector: 'app-ttm-details',
    templateUrl: './ttm-details.component.html',
    styleUrl: './ttm-details.component.scss'
})
export class TtmDetailsComponent extends CodaltComponent implements OnInit {

    @ViewChild('imageGallery') imageGallery: ElementRef<HTMLInputElement>;
    @Input() ttm: Ttm;
    @Input() ttmStatuses: TtmStatus[];
    @Input() ttmTypes: TtmType[];
    @Input() workorder: Workorder;
    @Input() gpsLocation: { lat: number; lng: number; };

    @Output() close = new EventEmitter<void>();
    @Output() delete = new EventEmitter<Ttm>();
    @Output() ticket = new EventEmitter<Ttm>();
    @Output() details = new EventEmitter<Ttm>();
    @Output() updateTtm = new EventEmitter<Ttm>();
    @Output() updateImages = new EventEmitter<TtmImage[]>();
    @Output() openLocation = new EventEmitter<any>();

    private _snackBar = inject(MatSnackBar);
    private isAtPhotoLocation = false;
    history = output<Ttm>();

    images: TtmImage[];
    ttmImageDistance = new Map<number, string>();

    weightingFields = ['weighting_one', 'weighting_two', 'weighting_three'];

    savingStatus = null;
    savingWeighting = null;

    fgComment = new FormGroup({
        comment: new FormControl<string>(''),
        comment_show_on_status_change: new FormControl<boolean>(true),
        comment_send_notification: new FormControl<boolean>(false),
        comment_show_on_map: new FormControl<boolean>(true),
    });
    savingComment = false;

    showDelete: TtmImage;
    longpressing = false;

    @HostListener('click', ['$event'])
    click(event) {
        if (event?.target?.className && ['image-list-wrap', 'details', 'ttm-holder', 'status', 'mat-mdc-input-element', 'toolbar'].some(className => event.target.className.includes(className))) {
            this.showDelete = null;
        }
    }

    constructor(private ngZone: NgZone,
                private cdref: ChangeDetectorRef,
                private ttmImageService: TtmImageService,
                private matDialog: MatDialog,
                private confirmDialog: ConfirmDialogService,
                private cameraPermissionService: CameraPermissionService,
                private ttmService: TtmService) {
        super();
    }

    ngOnInit() {
        this.fgComment.controls.comment.setValue(this.ttm.comment);
        this.fgComment.controls.comment_show_on_status_change.setValue(this.ttm.comment_show_on_status_change);
        this.fgComment.controls.comment_show_on_map.setValue(this.ttm.comment_show_on_map);
        this.subscriptions.add(this.ttmImageService.getTtmImages(this.ttm.id).subscribe(images => {
            this.images = images.data;
            this.images.forEach(image => {
                this.ttmImageDistance.set(image.id, Utils.ttmEntityDistance(this.ttm, image));
            });
        }));
        if (!AuthorisationService.hasFeature(Features.TtmsUpdateCommentSave)) {
            this.fgComment.disable();
            this.subscriptions.add(this.fgComment.valueChanges.pipe(debounceTime(500)).subscribe(() => {
                if (!this.savingComment) {
                    this.saveComment();
                }
            }));
        }
    }

    closeDialog() {
        if (this.fgComment.dirty) {
            this.confirmDialog.confirm(
                'De opmerking is niet opgeslagen',
                `De ingevulde opmerking is niet opgeslagen. Wilt u deze alsnog opslaan?`,
                'Opslaan',
                'Niet opslaan'
            ).then(() => {
                this.saveComment(true);
            }, () => {
                this.close.emit();
            });
        } else {
            this.close.emit();
        }
    }

    showImageDelete(event, image) {
        this.longpressing = true;
        event?.preventDefault();
        this.showDelete = image;
        setTimeout(() => {
            this.longpressing = false;
        }, 500);
    }

    deleteImage(event, image: TtmImage) {
        if (this.longpressing) {
            return;
        }
        this.confirmDialog.confirm(
            'Afbeelding verwijderen',
            `Weet je zeker dat je deze afbeelding wilt verwijderen?`,
            'Verwijderen',
            'Behouden'
        ).then(() => {
            this.ttmImageService.deleteImage(image.id).subscribe(() => {
                this.images.splice(this.images.indexOf(image), 1);
                this.updateImages.emit(this.images);
                this.ttm.ttm_images_count = (this.ttm.ttm_images_count ?? 0) - 1;
            });
        }, () => {
            this.showDelete = null;
        });
    }

    changeAmount() {
        const ref = this.matDialog.open(ChangeTtmAmountDialogComponent, {
            data: this.ttm,
        }).afterClosed().subscribe(ttm => {
            if (ttm) {
                this.updateTtm.emit(ttm);
            }
            ref.unsubscribe();
        });
    }

    saveComment(close = false) {
        this.savingComment = true;
        this.subscriptions.add(this.ttmService.updateComment(this.ttm.id, this.fgComment.value as any).subscribe(ttm => {
            Object.assign(this.ttm, ttm.data);
            this.fgComment.markAsPristine();
            this.savingComment = false;
            if (close) {
                this.close.emit();
            }
        }, () => {
            this.savingComment = false;
        }));
    }

    updateTtmWeightingField(value: boolean, field: string) {
        this.savingWeighting = field;
        this.subscriptions.add(this.ttmService.updateWeighting(this.ttm.id, field, value).subscribe(ttm => {
            this.ttm[field] = ttm.data[field];
            this.savingWeighting = null;
        }, () => {
            this.savingWeighting = null;
        }));
    }

    updateStatus() {
        this.subscriptions.add(this.matDialog.open(TtmStatusDialogComponent, {
            panelClass: 'confirm-dialog',
            disableClose: true,
            maxWidth: '98vw',
            width: '700px',
            data: {
                ttm: this.ttm,
                gpsLocation: this.gpsLocation
            }
        }).afterClosed().subscribe(newStatus => {
            if (newStatus) {
                this.ngZone.run(() => {
                    this.ttm.status_key = newStatus;
                    this.ttm.status_date = new Date();
                    this.ttm.status = this.ttmStatuses.find(ttmStatus => ttmStatus.key === this.ttm.status_key);
                });

                this.updateTtm.emit(this.ttm);
                this.subscriptions.add(this.ttmImageService.getTtmImages(this.ttm.id).subscribe(images => {
                    this.images = images.data;
                    this.updateImages.emit(this.images);
                    this.close.emit();
                    this.cdref.detectChanges();
                }));
            }
        }));
    }

    openImage(image: TtmImage) {
        if (this.showDelete !== image) {
            this.matDialog.open(ImageViewerComponent, {
                panelClass: 'image-viewer',
                width: '100vw',
                height: '100vh',
                maxWidth: '100vw',
                hasBackdrop: true,
                data: {
                    images: this.images,
                    currentImage: image,
                    ttmStatuses: this.ttmStatuses,
                    ttmImageDistance: this.ttmImageDistance
                }
            });
            this.showDelete = null;
        }
    }

    openTtmSvg(ttm: Ttm) {
        this.matDialog.open(TtmSvgViewerComponent, {
            panelClass: 'image-viewer',
            backdropClass: 'svg-viewer-backdrop',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            hasBackdrop: true,
            data: {
                ttms: [ttm],
                currentTtm: ttm
            }
        });
    }

    copyTtmLink(ttm: Ttm) {
        const url = new URL(this.env.apiEndpoint + `${Routenames.Projects}/` + ttm.project_id);
        url.searchParams.set('ttm', ttm.id.toString());
        url.searchParams.set('drawingId', ttm.entities[0]?.drawing_ids?.[0]?.toString() ?? '');

        navigator.clipboard.writeText(url.toString());
        this._snackBar.open('Link naar TVM gekopieerd naar klembord', null, {duration: 3000});

    }

    openCamera(input: HTMLInputElement) {
        this.cameraPermissionService.openInput(input, 'camera');
    }

    openGallery(input: HTMLInputElement) {
        this.cameraPermissionService.openInput(input, 'gallery');
    }

    uploadFile(event) {
        event.stopPropagation();
        event.preventDefault();
        const files = event.srcElement.files;
        if (files.length > 0) {
            const newImg = new TtmImage();
            this.images.splice(0, 0, newImg);
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.upload(file, newImg);
            }

        }
    }

    private upload(file, newImg: TtmImage) {
        Utils.resize((resizedFile) => {
            this.subscriptions.add(this.ttmImageService.uploadImage(resizedFile, this.ttm.status_key, this.ttm.id, this.gpsLocation)
                .subscribe(ttmImage => {
                    this.ttmImageDistance.set(ttmImage.data.id, Utils.ttmEntityDistance(this.ttm, ttmImage.data));
                    Object.assign(newImg, ttmImage.data);
                    this.ttm.ttm_images_count = (this.ttm.ttm_images_count ?? 0) + 1;
                    this.cdref.detectChanges();
                    this.updateImages.emit(this.images);

                }, (error: ApiErrorResponse) => {
                    const subs = this.matDialog.open(FileUploadFailDialogComponent, {
                        panelClass: 'confirm-dialog',
                        disableClose: true,
                        data: {file, error},
                        maxWidth: '450px'
                    }).afterClosed().subscribe(retry => {
                        if (retry) {
                            this.upload(file, newImg);
                        } else {
                            this.images.splice(this.images.indexOf(newImg), 1);
                        }
                        subs.unsubscribe();
                    });
                }));
            this.imageGallery.nativeElement.value = null;
        }, file, 1920);
    }

    onOpenLocation(index) {
        const currentImage = this.images[index];
        if (currentImage.location) {
            if (this.isAtPhotoLocation) {
                this.openLocation.emit(null);
                this.isAtPhotoLocation = false;
            } else {
                this.openLocation.emit(currentImage.location);
                this.isAtPhotoLocation = true;
            }
        }
    }

    protected readonly TtmTypes = TtmTypes;
}
